import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { WsConnectionState, WsEvent } from '../models/chat.model';

@Injectable()
export class AgentWsService implements OnDestroy {
  private ws: WebSocket | null = null;
  private events$ = new Subject<WsEvent>();
  private _sessionId = '';
  /** Connect args retained so a dropped socket can be reopened without the
   *  caller's involvement (see auto-reconnect below). Cleared on disconnect(). */
  private _wsUrl = '';
  private _agentId = '';
  /** True once the current socket completes its handshake (onopen). Lets us
   *  tell a rejected $connect (never opened) apart from a mid-stream drop. */
  private _opened = false;
  /** One-shot guard so a never-opened socket surfaces its failure only once. */
  private _connectFailed = false;
  /** True once ANY socket for the current connect() args has opened. Gates
   *  auto-reconnect: a connection that worked and then dropped (idle close,
   *  network blip, laptop wake) is retried silently; a handshake that never
   *  succeeded (rejected $connect) already surfaced via _failConnect and is
   *  not hammered. */
  private _everConnected = false;
  /** True while the current socket attempt is a silent auto-reconnect rather
   *  than a caller-initiated connect(); suppresses the never-opened failure
   *  message that would otherwise land in the chat on every failed retry. */
  private _reconnecting = false;
  private _reconnectAttempt = 0;
  private _reconnectHandle: number | null = null;
  private _heartbeatHandle: number | null = null;
  /** Latest frame a caller tried to send while the socket was down. Flushed on
   *  the next successful (re)connect; failed loudly if reconnect gives up.
   *  Single slot — the composer is isInFlight-gated, so at most one turn can
   *  be outstanding. */
  private _pendingFrame: string | null = null;

  /** Coalesced streaming text. AGENTHOME emits one `text_delta` per word-chunk
   *  (~tens to hundreds per reply); pushing each straight through re-parses the
   *  whole accumulated Markdown and runs change detection per token (O(n²) over a
   *  reply). We accumulate delta text here and flush it as ONE concatenated
   *  `text_delta` at most every DELTA_FLUSH_MS instead. This only changes how
   *  often the bubble re-renders mid-stream — the final text is identical (a
   *  trailing `text` event replaces it, or the full concatenation is preserved
   *  on `done`). */
  private _deltaBuffer = '';
  private _flushHandle: number | null = null;

  /** Minimum interval between coalesced text_delta flushes. A per-frame (rAF)
   *  flush still re-parses the full accumulated Markdown up to 60×/s on long
   *  replies; ~10 renders/s reads just as live while cutting that work ~6×. */
  private static readonly DELTA_FLUSH_MS = 100;

  /** API Gateway closes a WebSocket after 10 minutes without traffic — a fixed
   *  AWS limit, not ours (prod sockets died at exactly +10m of client silence;
   *  2026-08-04 "Simulated MSP keeps disconnecting" investigation). An
   *  application-level ping every 4 minutes keeps at least two frames inside
   *  every idle window. The WS router absorbs `{"action":"ping"}` before any
   *  dispatch, so a ping never reaches an agent. */
  private static readonly HEARTBEAT_MS = 240_000;

  /** Backoff schedule for silent auto-reconnects; the last step repeats until
   *  MAX_RECONNECT_ATTEMPTS, after which we go quiet and leave retrying to the
   *  visibility/online listeners or the next send(). */
  private static readonly RECONNECT_DELAYS_MS = [1_000, 2_000, 5_000, 10_000, 30_000];
  private static readonly MAX_RECONNECT_ATTEMPTS = 8;

  readonly connectionState$ = new BehaviorSubject<WsConnectionState>('disconnected');

  /** Reconnect promptly when the user returns to the tab or the network comes
   *  back. Timers don't tick during laptop sleep, so the post-wake experience
   *  hinges on these events, not on the backoff timer. */
  private readonly _onWake = () => {
    if (document.visibilityState !== 'visible') return;
    this._tryReconnectNow();
  };

  constructor(private readonly ngZone: NgZone) {
    this.ngZone.runOutsideAngular(() => {
      document.addEventListener('visibilitychange', this._onWake);
      window.addEventListener('online', this._onWake);
      window.addEventListener('focus', this._onWake);
    });
  }

  connect(wsUrl: string, sessionId: string, agentId: string = ''): Observable<WsEvent> {
    this.disconnect();
    // Bug 3 fix: fresh Subject per connection so stale subscribers never receive new events
    this.events$ = new Subject<WsEvent>();
    this._sessionId = sessionId;
    this._wsUrl = wsUrl;
    this._agentId = agentId;
    this._opened = false;
    this._connectFailed = false;
    this._everConnected = false;
    this._reconnecting = false;
    this._reconnectAttempt = 0;
    this._cancelFlush();
    this._deltaBuffer = '';

    if (!wsUrl) {
      this.connectionState$.next('error');
      throw new Error('WebSocket URL is not configured for this agent.');
    }

    this._openSocket();
    return this.events$.asObservable();
  }

  /** Open a socket for the stored connect args. Shared by connect() and the
   *  silent auto-reconnect path — the latter deliberately does NOT recreate
   *  events$, so subscribers survive a reconnect without re-subscribing. */
  private _openSocket(): void {
    this._opened = false;
    this.connectionState$.next('connecting');
    // Token read per attempt, not per connect(): a reconnect minutes later must
    // carry the freshest MSAL-refreshed token, not the one from page load.
    const idToken = sessionStorage.getItem('id-token') ?? '';
    const agentParam = this._agentId ? `&agent_id=${encodeURIComponent(this._agentId)}` : '';
    this.ws = new WebSocket(`${this._wsUrl}?session_id=${encodeURIComponent(this._sessionId)}&token=${encodeURIComponent(idToken)}${agentParam}`);

    this.ws.onopen = () => {
      this._opened = true;
      this._everConnected = true;
      this._reconnecting = false;
      this._reconnectAttempt = 0;
      // Partial text buffered before a mid-stream drop belongs to a turn the
      // component has already closed out — never leak it into a later turn.
      this._cancelFlush();
      this._deltaBuffer = '';
      this._startHeartbeat();
      this.connectionState$.next('connected');
      if (this._pendingFrame !== null) {
        const frame = this._pendingFrame;
        this._pendingFrame = null;
        this.ws?.send(frame);
      }
    };
    this.ws.onerror = () => { this.connectionState$.next('error'); this._failConnect(); };
    this.ws.onclose = () => {
      this._stopHeartbeat();
      if (this.connectionState$.value !== 'error') this.connectionState$.next('disconnected');
      this._failConnect();
      this._scheduleReconnect();
    };
    this.ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data as string) as WsEvent;
        if (data.type === 'text_delta') {
          // Accumulate; render at most once per DELTA_FLUSH_MS (see _deltaBuffer).
          this._deltaBuffer += data.content ?? '';
          this._scheduleFlush();
          return;
        }
        // Any non-delta event (text / done / suggestions / tool / error …) must
        // not jump ahead of buffered text — flush first, then emit, in one zone run.
        this.ngZone.run(() => {
          this._emitBufferedDeltas();
          this.events$.next(data);
        });
      } catch { /* ignore non-JSON frames */ }
    };
  }

  // ── Keepalive heartbeat ────────────────────────────────────────────────────

  private _startHeartbeat(): void {
    this._stopHeartbeat();
    this._heartbeatHandle = this.ngZone.runOutsideAngular(() =>
      window.setInterval(() => {
        if (this.ws?.readyState === WebSocket.OPEN) {
          this.ws.send(JSON.stringify({ action: 'ping' }));
        }
      }, AgentWsService.HEARTBEAT_MS),
    );
  }

  private _stopHeartbeat(): void {
    if (this._heartbeatHandle !== null) {
      clearInterval(this._heartbeatHandle);
      this._heartbeatHandle = null;
    }
  }

  // ── Silent auto-reconnect ──────────────────────────────────────────────────

  /** Arrange a silent retry after an unexpected close of a socket that had
   *  worked. Intentional teardown never gets here — disconnect() nulls the
   *  handlers before closing, so onclose doesn't fire for it. */
  private _scheduleReconnect(): void {
    if (!this._everConnected || !this._wsUrl || !this._sessionId) return;
    if (this._reconnectHandle !== null) return;
    if (this._reconnectAttempt >= AgentWsService.MAX_RECONNECT_ATTEMPTS) {
      // Go quiet — the wake listeners or the next send() will try again. A
      // frame captured while retrying must fail loudly, though: the component
      // is showing a thinking bubble for it and would otherwise hang forever.
      if (this._pendingFrame !== null) {
        this._pendingFrame = null;
        this._emitConnectionError('Not connected to the assistant. Please refresh and try again.');
      }
      return;
    }
    const delays = AgentWsService.RECONNECT_DELAYS_MS;
    const delay = delays[Math.min(this._reconnectAttempt, delays.length - 1)];
    this._reconnectAttempt++;
    this._reconnecting = true;
    this._reconnectHandle = this.ngZone.runOutsideAngular(() =>
      window.setTimeout(() => {
        this._reconnectHandle = null;
        this._openSocket();
      }, delay),
    );
  }

  /** Immediate reconnect attempt (wake listener / send-while-down). Resets the
   *  backoff — these are strong signals the network is back. No-op while a
   *  socket is already open or mid-handshake, or when reconnect isn't eligible. */
  private _tryReconnectNow(): void {
    if (!this._everConnected || !this._wsUrl || !this._sessionId) return;
    if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) return;
    if (this._reconnectHandle !== null) {
      clearTimeout(this._reconnectHandle);
      this._reconnectHandle = null;
    }
    this._reconnectAttempt = 0;
    this._reconnecting = true;
    this._openSocket();
  }

  // ── Delta coalescing ───────────────────────────────────────────────────────

  /** Schedule a single coalesced flush in DELTA_FLUSH_MS. Scheduled outside
   *  Angular so the pending timer doesn't keep the zone unstable; the flush
   *  re-enters the zone so the subscriber's signal update triggers CD. Non-delta
   *  events still flush ahead of themselves immediately (see onmessage). */
  private _scheduleFlush(): void {
    if (this._flushHandle !== null) return;
    this._flushHandle = this.ngZone.runOutsideAngular(() =>
      window.setTimeout(
        () => this.ngZone.run(() => this._emitBufferedDeltas()),
        AgentWsService.DELTA_FLUSH_MS,
      ),
    );
  }

  /** Emit buffered delta text as one event and clear any pending frame.
   *  Caller must already be inside ngZone.run. */
  private _emitBufferedDeltas(): void {
    this._cancelFlush();
    if (!this._deltaBuffer) return;
    const content = this._deltaBuffer;
    this._deltaBuffer = '';
    this.events$.next({ type: 'text_delta', content });
  }

  private _cancelFlush(): void {
    if (this._flushHandle !== null) {
      clearTimeout(this._flushHandle);
      this._flushHandle = null;
    }
  }

  // ── Send ───────────────────────────────────────────────────────────────────

  send(sessionId: string, message: string, extra?: Record<string, unknown>): void {
    const frame = JSON.stringify({ session_id: sessionId, message, ...extra });
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(frame);
      return;
    }
    // Socket is down but it worked before — an idle close or network blip the
    // heartbeat/backoff hasn't healed yet. Hold the frame and reconnect right
    // now; it flushes on open. Losing the frame here is exactly what used to
    // strand users behind "please refresh" after 10 idle minutes.
    if (this._everConnected && this._wsUrl && this._sessionId) {
      this._pendingFrame = frame;
      this._tryReconnectNow();
      return;
    }
    // Never connected — the $connect handshake was rejected (e.g. the WS
    // router returned 403). Silently dropping the frame here is exactly what
    // left the chat stuck "thinking" forever: no reply and no 'done' ever
    // arrive, so isInFlight never clears. Surface a turn-ending error + done
    // instead so the component clears its in-flight state, shows a message,
    // and lets the user retry.
    this._emitConnectionError('Not connected to the assistant. Please refresh and try again.');
  }

  /** Push a synthetic error + done into the event stream so subscribers treat
   *  the turn as failed (clear in-flight, show a message) instead of hanging.
   *  AGENTHOME pairs every error with a done; we mirror that contract here. */
  private _emitConnectionError(message: string): void {
    // WebSocket callbacks fire outside Angular's zone; run() so signal updates
    // in the subscriber trigger change detection.
    this.ngZone.run(() => {
      this._emitBufferedDeltas();  // surface any partial text before the error
      this.events$.next({ type: 'error', message });
      this.events$.next({ type: 'done' });
    });
  }

  /** Handshake never completed (rejected $connect / immediate close). Emit the
   *  failure once. A socket that DID open and later dropped is left to the
   *  component's connectionState$ handling, not this path. Silent reconnect
   *  attempts are also excluded — their failures must not land in the chat on
   *  every retry; a frame captured while retrying fails via _scheduleReconnect
   *  when the attempts run out. */
  private _failConnect(): void {
    if (this._opened || this._connectFailed || this._reconnecting) return;
    this._connectFailed = true;
    this._emitConnectionError('Couldn’t connect to the assistant. Please refresh and try again.');
  }

  disconnect(): void {
    // Drop any pending coalesced frame and partial buffer — the Subject is about
    // to complete, so emitting here would throw, and a mid-stream disconnect is an
    // abnormal end the component already handles via connectionState$.
    this._stopHeartbeat();
    if (this._reconnectHandle !== null) {
      clearTimeout(this._reconnectHandle);
      this._reconnectHandle = null;
    }
    this._reconnecting = false;
    this._reconnectAttempt = 0;
    this._everConnected = false;
    this._pendingFrame = null;
    this._wsUrl = '';
    this._agentId = '';
    this._cancelFlush();
    this._deltaBuffer = '';
    if (this.ws) {
      this.ws.onopen = null; this.ws.onmessage = null;
      this.ws.onerror = null; this.ws.onclose = null;
      if (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING) {
        this.ws.close();
      }
      this.ws = null;
    }
    this._sessionId = '';
    this.events$.complete();
    this.connectionState$.next('disconnected');
  }

  get currentSessionId(): string { return this._sessionId; }

  ngOnDestroy(): void {
    document.removeEventListener('visibilitychange', this._onWake);
    window.removeEventListener('online', this._onWake);
    window.removeEventListener('focus', this._onWake);
    this.disconnect();
  }
}
